import { MuiChipsInput } from 'mui-chips-input';
import { useState } from 'react';
import Label from './Label';
import { useLabelAttachmentsStore } from './labelAttachments.store';
import labelColors from './labelColors';
import { type LabelDef, useLabelDefinitionsStore } from './labelDefinitions.store';

/** Library limitations: the number of labels shown in the input is not controllable, the input does not shrink, and `value` must be `string[]`, so the color is determined later. */
type AddLabelInputProps = { handleSearchInputChange: (value: string) => void; firstOption?: LabelDef };

export default function AddLabelInput({ handleSearchInputChange, firstOption }: AddLabelInputProps) {
  const newLabelAttachments = useLabelAttachmentsStore((state) => state.newLabelAttachments);
  const addAttachment = useLabelAttachmentsStore((state) => state.addNewLabelAttachment);
  const removeAttachment = useLabelAttachmentsStore((state) => state.removeNewLabelAttachment);

  const addDefinition = useLabelDefinitionsStore((state) => state.addNewDefinition);
  const removeDefinition = useLabelDefinitionsStore((state) => state.removeNewLabelDefinition);
  const allLabels = useLabelDefinitionsStore((state) => state.getAllLabels());

  const getLabelColor = useLabelDefinitionsStore((state) => state.getLabelColor);

  const [inputValue, setInputValue] = useState('');

  const removeDraftLabel = (label: string) => {
    if (newLabelAttachments.some((attached) => attached.label === label)) {
      removeAttachment(label);
    } else {
      removeDefinition(label);
    }
  };

  return (
    <MuiChipsInput
      value={newLabelAttachments.map((label) => label.label)}
      onAddChip={(label) => {
        const newDefinition = { label, color: labelColors.colorFromLabel(label), id: null };

        addDefinition(newDefinition);
        addAttachment(newDefinition);
      }}
      hideClearAll
      onDeleteChip={removeDraftLabel}
      renderChip={(_Component, key, props) => (
        <Label
          key={key}
          label={props.label}
          color={getLabelColor(props.label)}
          onRemove={() => removeDraftLabel(props.label)}
        />
      )}
      inputValue={inputValue}
      onInputChange={(value) => {
        setInputValue(value);
        handleSearchInputChange(value);
      }}
      // prevent the MUI menu from selecting a menu item on keydown
      onKeyDown={(e) => {
        if (e.key === 'Tab' && firstOption) {
          addAttachment(firstOption);
          setInputValue('');
          handleSearchInputChange('');
          e.preventDefault();
        }

        e.stopPropagation();
      }}
      validate={(inputLabel) => {
        if (allLabels.has(inputLabel)) {
          return { isError: true, textError: 'Label already exists!' };
        }

        return { isError: false, textError: '' };
      }}
      placeholder=""
      sx={{
        lineHeight: '24px',
        width: '400px',
        ['& fieldset']: { border: 'none' },
        ['& .MuiInputBase-root']: { alignItems: 'center', backgroundColor: 'grey.50' },
      }}
      size="small"
    />
  );
}
